import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { dataExchangeApi } from "@/services/data-exchange-api.service";
import type {
  SyncPartnerResume,
  SyncHistoryOut,
  DataExchangeReport,
} from "@/types/api-v2";
import { Plus, Upload, RefreshCw, Loader2, UserPlus, Download } from "lucide-react";

export default function SyncManagementPanel() {
  const fileRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const [createOpen, setCreateOpen] = useState(false);
  const [partnersOpen, setPartnersOpen] = useState<number | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  async function downloadSyncDelta(partner: SyncPartnerResume) {
    try {
      const blob = await dataExchangeApi.downloadSyncDelta(partner.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gpm-sync-${partner.partner_code}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast({ title: "Дельта-пакет сформирован", description: partner.partner_code });
    } catch (e: any) {
      toast({
        title: "Ошибка формирования дельта-пакета",
        description: e?.message || "Не удалось выгрузить дельту",
        variant: "destructive",
      });
    }
  }

  const { data: partners = [], isLoading: loadingPartners } = useQuery({
    queryKey: ["sync-partners"],
    queryFn: () => dataExchangeApi.listSyncPartners(),
    refetchInterval: 5000,
  });

  const { data: history = [] } = useQuery({
    queryKey: ["sync-history"],
    queryFn: () => dataExchangeApi.listSyncHistory(undefined, 100),
    enabled: historyOpen,
  });

  return (
    <div className="space-y-6">
      <div className="rounded-lg border p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-md font-medium">Партнёры синхронизации</h3>
            <p className="text-sm text-muted-foreground">
              Standalone-обмен пакетами между изолированными системами (контуры Astra Linux).
            </p>
          </div>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Добавить партнёра
          </Button>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Код</TableHead>
              <TableHead>Название</TableHead>
              <TableHead>Приоритет</TableHead>
              <TableHead>Роль</TableHead>
              <TableHead>Prefix</TableHead>
              <TableHead>Последняя синхронизация</TableHead>
              <TableHead className="text-right">Действия</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loadingPartners ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-6 text-muted-foreground">
                  Загрузка…
                </TableCell>
              </TableRow>
            ) : partners.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-6 text-muted-foreground">
                  Партнёров нет — добавьте первого
                </TableCell>
              </TableRow>
            ) : (
              partners.map((p: SyncPartnerResume) => (
                <TableRow key={p.id}>
                  <TableCell className="font-mono">{p.partner_code}</TableCell>
                  <TableCell>{p.name}</TableCell>
                  <TableCell>{p.priority}</TableCell>
                  <TableCell>{p.role}</TableCell>
                  <TableCell className="font-mono">{p.remote_system_prefix ?? "—"}</TableCell>
                  <TableCell>
                    {p.last_sync_status ? (
                      <Badge className={p.last_sync_status === "failed" ? "bg-red-100 text-red-800" : "bg-teal-100 text-teal-800"}>
                        {p.last_sync_status}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                    {p.last_sync_timestamp ? (
                      <span className="ml-2 text-muted-foreground">
                        {new Date(p.last_sync_timestamp).toLocaleString("ru-RU")}
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button variant="outline" size="sm" onClick={() => downloadSyncDelta(p)}>
                      <Download className="mr-1 h-4 w-4" />
                      Дельта-пакет
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setPartnersOpen(p.id)}>
                      <Upload className="mr-1 h-4 w-4" />
                      Принять пакет
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="rounded-lg border">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-md font-medium">История синхронизаций</h3>
          <Button variant="ghost" size="sm" onClick={() => setHistoryOpen((v) => !v)}>
            <RefreshCw className="mr-1 h-4 w-4" />
            {historyOpen ? "Скрыть" : "Показать"}
          </Button>
        </div>
        {historyOpen && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Пакет</TableHead>
                <TableHead>Направл.</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead>Создано</TableHead>
                <TableHead>Обновлено</TableHead>
                <TableHead>Пропущено</TableHead>
                <TableHead>Медиа</TableHead>
                <TableHead>Дата</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-6 text-muted-foreground">
                    Синхронизаций пока не было
                  </TableCell>
                </TableRow>
              ) : (
                history.map((h: SyncHistoryOut) => (
                  <TableRow key={h.id}>
                    <TableCell className="max-w-[220px] truncate">{h.package_name}</TableCell>
                    <TableCell>{h.direction}</TableCell>
                    <TableCell>
                      <Badge
                        className={
                          h.status === "failed"
                            ? "bg-red-100 text-red-800"
                            : h.status === "conflicts"
                              ? "bg-amber-100 text-amber-800"
                              : "bg-green-100 text-green-800"
                        }
                      >
                        {h.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{h.objects_created}</TableCell>
                    <TableCell>{h.objects_updated}</TableCell>
                    <TableCell>{h.objects_skipped}</TableCell>
                    <TableCell>{h.media_count}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {new Date(h.created_at).toLocaleString("ru-RU")}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </div>

      <CreatePartnerDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      {partnersOpen !== null && (
        <UploadPackageDialog
          partnerId={partnersOpen}
          onClose={() => setPartnersOpen(null)}
        />
      )}
      <input ref={fileRef} type="file" accept=".zip" className="hidden" />
    </div>
  );
}

function CreatePartnerDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [prefix, setPrefix] = useState("");
  const [priority, setPriority] = useState(0);
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!code.trim() || !name.trim()) {
      toast({ title: "Заполните код и название", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await dataExchangeApi.createSyncPartner({
        partner_code: code.trim(),
        name: name.trim(),
        description: "Партнёр синхронизации",
        priority,
        direction: "bi",
        role: "peer",
        remote_system_prefix: prefix.trim() || null,
        is_active: true,
      });
      toast({ title: "Партнёр создан", description: code });
      queryClient.invalidateQueries({ queryKey: ["sync-partners"] });
      onClose();
    } catch (e: any) {
      toast({
        title: "Ошибка",
        description: e?.message || "Не удалось создать партнёра",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Новый партнёр синхронизации</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Код партнёра *</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="STD" />
          </div>
          <div className="space-y-1">
            <Label>Название *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Внешняя система" />
          </div>
          <div className="space-y-1">
            <Label>Префикс source_system</Label>
            <Input value={prefix} onChange={(e) => setPrefix(e.target.value)} placeholder="Напр. SYP-STD" />
          </div>
          <div className="space-y-1">
            <Label>Приоритет при конфликтах (больше — выше)</Label>
            <Input
              type="number"
              value={priority}
              onChange={(e) => setPriority(Number(e.target.value) || 0)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />}
            Создать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UploadPackageDialog({ partnerId, onClose }: { partnerId: number; onClose: () => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [dryRun, setDryRun] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [report, setReport] = useState<DataExchangeReport | null>(null);

  async function upload() {
    if (!file) return;
    setProcessing(true);
    try {
      const rep = await dataExchangeApi.uploadSyncPackage(partnerId, file, dryRun);
      setReport(rep);
      toast({
        title: rep.status === "committed" ? "Пакет применён" : "Обработка завершена",
        description: `Статус: ${rep.status}`,
      });
      queryClient.invalidateQueries({ queryKey: ["sync-partners"] });
      queryClient.invalidateQueries({ queryKey: ["sync-history"] });
    } catch (e: any) {
      const detail = (e as any)?.detail;
      if (detail && typeof detail === "object" && "status" in detail) {
        setReport(detail as DataExchangeReport);
      }
      toast({
        title: "Ошибка обработки пакета",
        description: e?.message || "Не удалось обработать sync-пакет",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Приём sync-пакета от партнёра #{partnerId}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <Label>Архив .zip</Label>
              <Input
                type="file"
                accept=".zip"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </div>
            <div className="flex items-end gap-2">
              <div className="flex items-center gap-2 pb-2">
                <Switch id="sync-dry" checked={dryRun} onCheckedChange={setDryRun} />
                <Label htmlFor="sync-dry">Dry-run (без записи)</Label>
              </div>
              <Button onClick={upload} disabled={processing || !file}>
                {processing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                Обработать пакет
              </Button>
            </div>
          </div>

          {report && (
            <ScrollArea className="max-h-72 rounded-md border p-3 text-sm font-mono">
              <pre>{JSON.stringify(report, null, 2)}</pre>
            </ScrollArea>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Закрыть
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}